/* =========================================================================
   Ilene Joy — Price Lock client logic
   =========================================================================
   Wires the price-lock CTA in the charm builder to a checkout flow that:
     1. Snapshots the customer's current design configuration (full URL
        state + computed total) into line-item properties on a single
        line-item: the $99 Price Lock fee.
     2. Redirects to the native Shopify checkout carrying only that $99
        line item. The design items are NOT charged here — they're
        reconstructed when the customer returns to complete purchase
        within 48h (see /pages/price-lock-terms).
     3. A Shopify Flow automation on orders/create (set up separately
        by the merchant) reads the `_locked_*` properties, generates a
        discount code for the difference between today's design price
        and future checkout prices, and emails the customer a resume
        link + the discount code.

   Signed-in state is required by the server-rendered snippet — this
   script only runs when the customer is logged in. Logged-out state
   renders a sign-in link directly in the Liquid.
   ========================================================================= */

(function () {
  'use strict';

  /* Lock fee variant id — hardcoded from the Admin API creation step.
     If the fee product is rebuilt, update this constant to match. */
  var LOCK_FEE_VARIANT_ID = '55652306649161';
  var LOCK_WINDOW_HOURS = 48;

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function money(cents) { return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

  /* Reads the designed-piece total from the React app's rendered DOM.
     We prefer the compact checkout-total's ij__price-flash element since
     our lariat correction shim adjusts that one to the true cart-charge
     price. Falls back to chain-only if the React total element is missing. */
  function readCurrentDesignTotalCents() {
    var el = qs('.ij__checkout-compact-total .ij__price-flash')
          || qs('.ij__checkout-total .ij__price-flash');
    if (el) {
      var m = el.textContent.match(/\$([\d,]+(?:\.\d{2})?)/);
      if (m) return Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
    }
    return null;
  }

  /* Pull the current full URL (without origin) so the server side can
     reconstruct the customer's exact config when they return to checkout.
     URL includes cv, mm, l, s params — everything needed. */
  function currentConfigUrl() {
    return location.pathname + location.search;
  }

  function mountModal() {
    var modal = qs('[data-ij-lock-modal]');
    if (!modal) return;
    var openBtn = qs('[data-ij-lock-open]');
    var confirmBtn = qs('[data-ij-lock-confirm]', modal);
    var closers = modal.querySelectorAll('[data-ij-lock-close]');
    var totalEl = qs('[data-ij-lock-design-total]', modal);

    function open() {
      /* Refresh the displayed total every open — price may have changed
         if the customer edited their design since the page loaded. */
      var cents = readCurrentDesignTotalCents();
      if (totalEl) totalEl.textContent = cents != null ? money(cents) : '—';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ij-modal-open');
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ij-modal-open');
    }

    if (openBtn) openBtn.addEventListener('click', open);
    closers.forEach(function (c) { c.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        if (confirmBtn.classList.contains('is-loading')) return;
        confirmBtn.classList.add('is-loading');
        submitLock(openBtn)
          .catch(function (err) {
            console.error('[ij-lock] submission failed', err);
            alert('Could not create the lock. Please try again or email shop@ilenejoy.com.');
          })
          .finally(function () { confirmBtn.classList.remove('is-loading'); });
      });
    }
  }

  function submitLock(openBtn) {
    var customerId = openBtn ? openBtn.getAttribute('data-customer-id') : '';
    var customerEmail = openBtn ? openBtn.getAttribute('data-customer-email') : '';
    var designTotalCents = readCurrentDesignTotalCents();
    var expiresAt = new Date(Date.now() + LOCK_WINDOW_HOURS * 3600 * 1000).toISOString();

    /* Everything the fulfillment flow needs to resume the customer's
       design in 48h is packed into underscore-prefixed properties:
       - `_locked_design_url` rebuilds the configurator state
       - `_locked_design_total_cents` is the price we promised them
       - `_locked_expires_at` is the hard deadline
       - `_locked_customer_id/email` links the lock to the account so
         Flow can look up the customer record and metafield-tag them */
    var properties = {
      '_locked_design_url': currentConfigUrl(),
      '_locked_design_total_cents': String(designTotalCents || 0),
      '_locked_expires_at': expiresAt,
      '_locked_customer_id': customerId || '',
      '_locked_customer_email': customerEmail || '',
      '_lock_fee_cents': '9900',
    };

    var form = new FormData();
    form.append('items[0][id]', LOCK_FEE_VARIANT_ID);
    form.append('items[0][quantity]', '1');
    Object.keys(properties).forEach(function (k) {
      form.append('items[0][properties][' + k + ']', properties[k]);
    });

    /* Use a FRESH cart for the lock — we don't want to accidentally
       charge the customer for their designed piece items right now. If
       they had design items in their cart, we need to clear first. */
    return fetch('/cart/clear.js', { method: 'POST' })
      .then(function () {
        return fetch('/cart/add.js', { method: 'POST', body: form });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add.js returned ' + r.status);
        return r.json();
      })
      .then(function () {
        /* Go straight to checkout — no cart page, no drawer. The fee is
           the entire cart content, by design. */
        window.location.href = '/checkout';
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountModal);
  } else {
    mountModal();
  }
})();
